import cn from 'classnames';
import React, {
  ComponentProps,
  ComponentPropsWithoutRef,
  FC,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import {
  DefaultValues,
  EventType,
  FieldError,
  FieldErrors,
  FieldPath,
  FieldPathValue,
  FieldValues,
  SubmitErrorHandler,
} from 'react-hook-form';

import ReadOnly from 'assets/icon/read-only.svg';
import MaterialIcon from 'components/atoms/MaterialIcon';
import Text from 'components/atoms/text';
import { FloatingTooltipText } from 'components/molecules/tooltipFloating';
import { ObjectPaths, ObjectPathsAsString } from 'utils/objectPaths';

import { FormEnvironmentBase } from './Form/formEnvironment';
import { useFormWatch } from './Form/useFormWatch';
import styles from './styles.module.scss';
import { Validator } from './validators';

export type FileFieldType = string | File | undefined;
export type FileFieldOnChange = (newValue: FileFieldType) => void;
export type HandleUpdateDocumentOnViewer = (blob: Blob) => void;

export type TypedFieldName<TForm> = ObjectPaths<TForm>;
export type FieldName<TForm = Record<string, any>> = TypedFieldName<TForm>;

export type SharedProps<TForm> = {
  fieldName: FieldName<TForm>;
  required?: boolean;
  description?: string;
  disabled?: boolean;
  readOnly?: boolean;
  maxLength?: number;
} & Parameters<typeof Label>[0];

export type InputActionIconProps = {
  iconName?: ComponentProps<typeof MaterialIcon>['name'];
  iconOnClick?: () => void;
  iconLabel?: string;
};

/*
  Allow for custom error messages for regular expression patterns
*/
export interface RegExpPattern {
  regExp: RegExp;
  errorMessage?: string;
}

type OptionType = string;

export type SelectOption<T extends OptionType = string> = {
  label: string;
  description?: string;
  disabled?: boolean;
  value: T;
};

export type SelectOptionGroup<T extends OptionType = string> = {
  label: string;
  disabled?: boolean;
  value: Array<SelectOption<T>>;
};

export const isSelectOptionGroup = <T extends OptionType>(
  value: SelectOption<T> | SelectOptionGroup<T>
): value is SelectOptionGroup<T> => {
  return Array.isArray(value.value);
};

export type SelectOptions<T extends OptionType = string> = Array<
  SelectOption<T> | SelectOptionGroup<T>
>;

export const spreadOptions = <T extends OptionType>(
  options: SelectOptions<T>
): SelectOption<T>[] =>
  options.reduce((accum, curr) => {
    if (isSelectOptionGroup(curr)) {
      accum.push(
        ...curr.value.map((item) => ({
          ...item,
          disabled: curr.disabled || item.disabled,
        }))
      );
    } else {
      accum.push(curr);
    }

    return accum;
  }, [] as SelectOption<T>[]);

export type OnBlurEvent = (e?: React.FocusEvent<HTMLInputElement>) => void;

export type InputOptionProps<TOptionType extends string = string> = {
  options?: Array<SelectOption<TOptionType>>;
  onlyOptionValues?: boolean;
};

export type InputProps<
  TForm,
  TFieldInputType,
  TOptionType extends string = string,
> = Override<
  ComponentPropsWithoutRef<'input'>,
  {
    type?: TFieldInputType;
    pattern?: RegExpPattern;
    description?: string;
    onBlur?: OnBlurEvent;
  } & SharedProps<TForm> &
    InputActionIconProps &
    InputOptionProps<TOptionType>
>;

export type TextAreaProps<TForm> = Omit<
  InputHTMLAttributes<HTMLTextAreaElement>,
  'onDragStart' | 'onDrag' | 'onDrop'
> &
  SharedProps<TForm> & {
    textareaRef?: (ref: HTMLTextAreaElement) => void;
    isReadOnly?: boolean;
    withInputHeight?: boolean;
    postIcon?: ReactNode;
    withAutoFocus?: boolean;
    mask?: (string | RegExp)[] | Function;
    showCharCounter?: boolean;
    description?: string;
    resizable?: boolean;
  };

export type SelectProps<TForm> = Override<
  ComponentPropsWithoutRef<'select'>,
  {
    options: SelectOptions;
    description?: string;
    uniqueFields?: string[];
  } & SharedProps<TForm>
> & {
  showErrorsOnMount?: boolean;
};

export type RadioProps<TForm, OptionType extends string = string> = Override<
  ComponentPropsWithoutRef<'input'>,
  {
    optionsMemoized: Array<SelectOption<OptionType>>;
    description?: string;
    uniqueFields?: string[];
    withUnderline?: boolean;
    handleDeleteOptionMemoized?: (value: string) => void;
    allowDeleteFirstOption?: boolean;
  } & SharedProps<TForm>
>;

export type FormArrayItem<T, TFormModel = Record<string, any>> = T & {
  id: string;
  prefix: ObjectPaths<TFormModel>;
  remove: () => void;
  update: (newValue: T) => void;
};

interface FormArrayRendererPropsBase<TModel, TFormModel = Record<string, any>> {
  itemsToRender: FormArrayItem<TModel, TFormModel>[];
  append: (initialValue: TModel) => void;
  clear: () => void;
  move: (from: number, to: number) => void;
  getValues: () => TModel[];
}

export type FormArrayRendererProps<
  TModel,
  TAdditionalProps = {},
  TFormModel = Record<string, any>,
> = FormArrayRendererPropsBase<TModel, TFormModel> & TAdditionalProps;

/**
 * This type replaces the WatchObserver type from react-hook-form, which incorrectly marks value as a DeepPartial<FormType>
 *
 * See https://github.com/react-hook-form/react-hook-form/issues/9919 for details and updates
 */
type FullWatchObserver<FormType extends FieldValues> = (
  value: FormType,
  info: {
    name?: FieldPath<FormType>;
    type?: EventType;
  }
) => void;

export type FormError = FieldError;
export type FormErrors = FieldErrors;

export type FormSubmitHandler<TFieldValues> = (
  data: TFieldValues,
  formEnvironment: FormEnvironmentBase<TFieldValues>
) => void | Promise<void>;

export type FormProps<FormType extends FieldValues> = Override<
  Pick<ComponentPropsWithoutRef<'form'>, 'children' | 'className'>,
  {
    onSubmit?: FormSubmitHandler<FormType>;
    onError?: SubmitErrorHandler<FormType>;
    onChange?: FullWatchObserver<FormType>;
    onValidChange?: (isValid: boolean, errors: FieldErrors<FormType>) => void;
    defaultValues?: DefaultValues<FormType>;
    readonly?: boolean;
    validateDefaultValues?: boolean;
    shouldUnregister?: boolean;
  }
>;

export interface FormArrayProps<
  TModel,
  TAdditionalProps = {},
  TFormModel = Record<string, any>,
> {
  name: ObjectPaths<TFormModel>;
  additionalProps: TAdditionalProps;
  renderer: FC<FormArrayRendererProps<TModel, TAdditionalProps, TFormModel>>;
}

export type TPropsWrapper<TModel, TProps = {}> = TProps & {
  name: string;
  onChange: (value: TModel) => void;
  value: TModel;
  onBlur: () => void;
};

export interface SwitchInputProps<TForm = Record<string, any>> {
  states: {
    left: LabelValue;
    right: LabelValue;
  };
  fieldName: ObjectPathsAsString<TForm>;
}

export function Label({
  label,
  required,
  withBoldWeight,
  readOnly,
  unsetWidth,
}: {
  label?: string | JSX.Element;
  required?: boolean;
  withBoldWeight?: boolean;
  readOnly?: boolean;
  unsetWidth?: boolean;
  disabled?: boolean;
  onCopyClick?: EmptyArgsVoidFunc;
}) {
  return (
    <div
      className={cn(styles.label, {
        [styles.UnsetWidth]: unsetWidth,
      })}
    >
      {typeof label === 'string' ? (
        <Text content={label} withBoldWeight={withBoldWeight} />
      ) : (
        label
      )}
      {readOnly && (
        <FloatingTooltipText title={'Read-only field'} useDefaultWidthAndHeight>
          <img className={styles.Img} src={ReadOnly} alt={'Read-only field'} />
        </FloatingTooltipText>
      )}
      {required && (
        <FloatingTooltipText title={'Required field'} useDefaultWidthAndHeight>
          {' '}
          <div className={styles.indicator}>*</div>
        </FloatingTooltipText>
      )}
      {/*{(disabled || readOnly) && (*/}
      {/*  <button type={'button'} onClick={onCopyClick}>*/}
      {/*    <CopyIcon />*/}
      {/*  </button>*/}
      {/*)}*/}
    </div>
  );
}

export interface ListFieldProps<TForm, T extends string = string> {
  fieldName: FieldName<TForm>;
  label: string;

  options?: SelectOption<T>[];
  initialValues?: T[];
  readonly?: boolean;
  minimumToDelete?: number;
  validators?: Array<Validator<Array<string>>>;
  allowSortList?: boolean;
}

export function FormValueProvider<
  FieldsTypes extends FieldValues,
  RenderExtraOpts,
>({
  selectedField,
  toRender,
  extraOptions,
}: {
  selectedField: ObjectPaths<FieldsTypes>;
  toRender: (
    value: FieldPathValue<FieldsTypes, FieldPath<FieldsTypes>> | undefined,
    options: RenderExtraOpts
  ) => ReactNode;
  extraOptions: RenderExtraOpts;
}): JSX.Element {
  const val = useFormWatch<
    FieldPathValue<FieldsTypes, FieldPath<FieldsTypes>> | undefined,
    FieldsTypes
  >(selectedField);

  return <>{toRender(val, extraOptions)}</>;
}
